import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { connectInfiniteHits, Highlight } from 'react-instantsearch-dom';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import config from '../../../config';
import { selectSettings } from '../../../store/selectors/settingsSelectors';

const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
`;

const ProductRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? themeColors.colorPrimary : themeColors.colorBorderPrimary};
  border-radius: 6px;
  background: ${themeColors.colorWhite};
  width: 100%;
  cursor: pointer;
  text-align: left;
  flex-shrink: 0;
`;

const ProductLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const Radio = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${themeColors.colorPrimary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $selected }) =>
      $selected ? themeColors.colorPrimary : 'transparent'};
  }
`;

const Thumb = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 4px;
`;

const ThumbPlaceholder = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  background: ${themeColors.colorBackgroundSecondary};
  flex-shrink: 0;
`;

const ProductName = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 18px;
  color: ${themeColors.colorTextPrimary};

  .ais-Highlight-highlighted {
    background: ${themeColors.colorWarningnBackground};
    color: ${themeColors.colorTextPrimary};
    font-weight: 700;
  }
`;

const ProductWeight = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${themeColors.colorLabel};
  flex-shrink: 0;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  min-height: 0;
  padding: 16px;
  text-align: center;

  > svg {
    width: 40px;
    height: 40px;
  }
`;

const EmptyMessage = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 18px;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;

const SupportLink = styled.a`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorPrimary};
  text-decoration: underline;
`;

const ProductThumb = ({ productCode, rootUrl }) => {
  const [hasError, setHasError] = useState(false);
  if (hasError || !productCode) return <ThumbPlaceholder />;
  const src = config.algoliaSearch.imageCode
    .replace('{product_code}', productCode)
    .replace('{base_url}', rootUrl);
  return (
    <Thumb
      src={encodeURI(src)}
      alt=""
      loading="eager"
      decoding="async"
      onError={() => setHasError(true)}
    />
  );
};

ProductThumb.propTypes = {
  productCode: PropTypes.string,
  rootUrl: PropTypes.string,
};

const SellFromHomeProductList = ({ hits, selectedCode, onSelect }) => {
  const { t } = useTranslation();
  const { RootUrl } = useSelector(selectSettings);
  const phone = t('sellWizard.sfhWizard.phone');

  const validHits = hits.filter((h) => h.Caption && h.Caption.trim());

  if (validHits.length === 0) {
    return (
      <EmptyState>
        <InfoIcon />
        <EmptyMessage>{t('sellWizard.sfhWizard.noResultsTitle')}</EmptyMessage>
        <SupportLink href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
          {t('sellWizard.sfhWizard.needHelp')} {phone}
        </SupportLink>
      </EmptyState>
    );
  }

  return (
    <ListWrap>
      {validHits.map((hit) => {
        const isSelected = selectedCode === hit.Code;
        return (
          <ProductRow
            type="button"
            key={hit.objectID}
            $selected={isSelected}
            onClick={() => onSelect(hit)}
          >
            <ProductLeft>
              <Radio $selected={isSelected} />
              <ProductThumb productCode={hit.Code} rootUrl={RootUrl} />
              <ProductName>
                <Highlight attribute="Caption" hit={hit} />
              </ProductName>
            </ProductLeft>
            <ProductWeight>{hit.WeightCaption}</ProductWeight>
          </ProductRow>
        );
      })}
    </ListWrap>
  );
};

SellFromHomeProductList.propTypes = {
  hits: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedCode: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default connectInfiniteHits(SellFromHomeProductList);
